import React from 'react'
import { BarChart3, CheckCircle, XCircle, TrendingUp, Award } from 'lucide-react'
import { MultiOptionAnalysis as MultiOptionAnalysisType } from '../types'

interface MultiOptionAnalysisProps {
  result: MultiOptionAnalysisType | null
}

export const MultiOptionAnalysis: React.FC<MultiOptionAnalysisProps> = ({
  result
}) => {

  console.log('=== MultiOptionAnalysis Component ===')
  console.log('Result received:', result)
  console.log('Result type:', typeof result)
  console.log('Result structure:', result ? JSON.stringify(result, null, 2) : 'null')

  if (!result) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-4">
          <BarChart3 className="w-16 h-16 mx-auto" />
        </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Multi-option Analysis</h3>
        <p className="text-gray-600">
                      Click the start analysis button to compare multiple options and find the best choice
        </p>
      </div>
    )
  }

  // 数据验证和错误处理
  if (typeof result === 'string') {
    console.error('Result is a string, not an object:', result)
    return (
      <div className="text-center py-8">
        <div className="text-red-400 mb-4">
          <BarChart3 className="w-16 h-16 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-red-900 mb-2">Data Format Error</h3>
        <p className="text-red-600">
          The analysis result is not in the expected format. Please try again.
        </p>
        <div className="mt-4 p-4 bg-gray-100 rounded text-left text-sm">
          <pre className="whitespace-pre-wrap">{result}</pre>
        </div>
      </div>
    )
  }

  // 检查必要的数据结构
  if (!result.options || !Array.isArray(result.options)) {
    console.error('Missing or invalid options array:', result)
    return (
      <div className="text-center py-8">
        <div className="text-red-400 mb-4">
          <BarChart3 className="w-16 h-16 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-red-900 mb-2">Invalid Data Structure</h3>
        <p className="text-red-600">
          The analysis result is missing required data. Please try again.
        </p>
      </div>
    )
  }

  if (!result.recommendation || !result.recommendation.bestOption) {
    console.error('Missing recommendation data:', result)
    return (
      <div className="text-center py-8">
        <div className="text-red-400 mb-4">
          <BarChart3 className="w-16 h-16 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-red-900 mb-2">Missing Recommendation</h3>
        <p className="text-red-600">
          The analysis result is missing recommendation data. Please try again.
        </p>
      </div>
    )
  }

  const maxScore = Math.max(...result.options.map(opt => opt.score))

  return (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-medium text-green-900">Multi-option Analysis Complete</h3>
        </div>
        <p className="text-green-800">
          Based on the criteria you provided, here is a detailed comparative analysis of each option.
        </p>
      </div>

                  {/* Recommendation Result */}
      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Award className="w-6 h-6 text-orange-600" />
                      <h4 className="text-xl font-medium text-orange-900">Recommended Option</h4>
        </div>
        <div className="bg-white rounded-lg p-4 border border-orange-200">
          <h5 className="text-lg font-medium text-gray-900 mb-2">
            {result.recommendation.bestOption}
          </h5>
          <div className="flex items-center space-x-4 mb-3">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Confidence:</span>
              <div className="flex-1 w-24 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-orange-600 h-2 rounded-full" 
                  style={{ width: `${result.recommendation.confidence}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {result.recommendation.confidence}%
              </span>
            </div>
          </div>
          <p className="text-gray-700">{result.recommendation.reasoning}</p>
        </div>
      </div>

                  {/* Detailed Option Analysis */}
      <div className="space-y-4">
                  <h4 className="text-lg font-medium text-gray-900">Detailed Analysis</h4>
        {result.options.map((option) => (
          <div key={option.id} className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h5 className="text-lg font-medium text-gray-900 mb-1">
                  {option.text}
                </h5>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Overall Score:</span>
                    <span className="text-lg font-bold text-gray-900">{option.score}/100</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Rank:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {result.options
                        .sort((a, b) => b.score - a.score)
                        .findIndex(opt => opt.id === option.id) + 1}
                    </span>
                  </div>
                </div>
              </div>
              {option.score === maxScore && (
                <div className="flex items-center space-x-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                  <TrendingUp className="w-3 h-3" />
                                          <span>Highest Score</span>
                </div>
              )}
            </div>

            {/* Score Bar */}
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-sm text-gray-600">Score:</span>
                <div className="flex-1 bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-orange-600 h-3 rounded-full transition-all duration-300" 
                    style={{ width: `${option.score}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-900">{option.score}%</span>
              </div>
            </div>

            {/* Pros and Cons Analysis */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <h6 className="text-sm font-medium text-green-700 mb-2 flex items-center space-x-1">
                  <CheckCircle className="w-4 h-4" />
                  <span>Pros</span>
                </h6>
                <ul className="text-sm text-gray-600 space-y-1">
                  {option.pros.map((pro, i) => (
                    <li key={i} className="flex items-start space-x-1">
                      <span className="text-green-600 mt-1">•</span>
                      <span>{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h6 className="text-sm font-medium text-red-700 mb-2 flex items-center space-x-1">
                  <XCircle className="w-4 h-4" />
                  <span>Cons</span>
                </h6>
                <ul className="text-sm text-gray-600 space-y-1">
                  {option.cons.map((con, i) => (
                    <li key={i} className="flex items-start space-x-1">
                      <span className="text-red-600 mt-1">•</span>
                      <span>{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Criteria Score Matrix */}
            <div>
                              <h6 className="text-sm font-medium text-gray-700 mb-2">Criteria Scores</h6>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {result.comparisonCriteria.map((criteria) => (
                  <div key={criteria.id} className="text-xs">
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-600">{criteria.name}</span>
                      <span className="font-medium text-gray-900">
                        {option.comparisonMatrix[criteria.name] || 0}/10
                      </span>
                    </div>
                    <div className="bg-gray-200 rounded-full h-1">
                      <div 
                        className="bg-blue-600 h-1 rounded-full" 
                        style={{ width: `${((option.comparisonMatrix[criteria.name] || 0) / 10) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

                  {/* Comparison Criteria */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Comparison Criteria</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {result.comparisonCriteria.map((criteria) => (
            <div key={criteria.id} className="p-3 bg-white rounded border border-gray-200">
              <div className="flex items-center justify-between mb-1">
                <h5 className="font-medium text-gray-900">{criteria.name}</h5>
                <span className="text-sm text-gray-600">Weight: {criteria.weight}/10</span>
              </div>
              <p className="text-sm text-gray-600">{criteria.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Analysis Guide</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Each option is scored based on the criteria you set</li>
          <li>• Overall score considers the weight of each criterion</li>
          <li>• Pros and cons analysis helps you understand each option comprehensively</li>
          <li>• Criteria score matrix shows how each option performs under different criteria</li>
        </ul>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={() => window.location.reload()}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Start New Analysis
        </button>
      </div>
    </div>
  )
} 